/**
 * Error code mapping for ExternalMatchRequestRules validation errors
 */
export const ERROR_MESSAGE_MAP: Record<string, string> = {
  MIN_AMOUNT_INVALID: 'Minimum amount must be $0 or greater. Please enter a valid minimum amount.',
  MAX_AMOUNT_INVALID: 'Maximum amount must be at least $0.01. Please enter a valid maximum amount.',
  MAX_LESS_THAN_MIN:
    'Maximum amount must be greater than the minimum amount. Please adjust your donation amount range.',
  CONFIDENCE_SCORE_INVALID:
    'AI confidence score must be between 0 and 1. Please enter a valid confidence score.',
  START_DATE_INVALID: 'Invalid date format for start date. Please select a valid date.',
  END_DATE_INVALID: 'Invalid date format for end date. Please select a valid date.',
  DATES_MUST_BE_TOGETHER:
    'Both start and end dates must be provided together. Please select both dates or leave both blank.',
  START_AFTER_END: 'Start date must be before the end date. Please adjust your date range.',
  EMPLOYEE_LOCATIONS_INVALID:
    'One or more employee locations are invalid. Please try reselecting the locations.',
  VALIDATION_ERROR: 'The rule failed validation. Please check all fields and try again.',
  UPDATE_FAILED: 'Failed to update the rule. Please try again in a few moments.',
  CREATE_FAILED: 'Failed to create the rule. Please try again in a few moments.',
};

export const DEFAULT_ERROR_MESSAGE =
  'An unexpected error occurred. Please try again, or contact support if the issue persists.';

export const AUTHORIZATION_ERROR_MESSAGE =
  'You do not have permission to perform this action. Please contact your administrator.';

/**
 * Helper to extract user-friendly error messages from GraphQL errors
 * for the ExternalMatchRequestRules feature.
 * @param error - The error object to extract the user-friendly message from.
 * @returns The user-friendly message. Returns default error message if the error is not a string, empty, or does not contain a valid error code.
 */
export function getUserFriendlyErrorMessage(error: unknown): string {
  if (!error) return DEFAULT_ERROR_MESSAGE;

  if (typeof error === 'string') return error;

  const errorObj = error as Record<string, unknown>;
  const graphQLErrors = errorObj.graphQLErrors as Array<{ message?: string }> | undefined;
  const errorMessage = graphQLErrors?.[0]?.message || (errorObj.message as string) || '';

  if (!errorMessage) return DEFAULT_ERROR_MESSAGE;

  // Extract error code from GraphQL error
  // Format: "BadRequest: ERROR_CODE"
  const badRequestMatch = errorMessage.match(/BadRequest:\s*(.+)/);
  if (badRequestMatch) {
    const errorCode = badRequestMatch[1].trim();
    return ERROR_MESSAGE_MAP[errorCode] || DEFAULT_ERROR_MESSAGE;
  }

  // Handle authorization errors
  if (errorMessage.includes('Unauthorized')) {
    return AUTHORIZATION_ERROR_MESSAGE;
  }

  // For all other errors, show generic message
  return DEFAULT_ERROR_MESSAGE;
}
